import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, PageLoader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Cloud, Save, CheckCircle2, XCircle, AlertTriangle, HardDrive, Plug, Loader2 } from "lucide-react";

function Health({ h }) {
  return h?.ok
    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-4 w-4" /> {h.detail}</span>
    : <span className="inline-flex items-center gap-1 text-xs text-rose-400"><XCircle className="h-4 w-4" /> {h?.detail || "down"}</span>;
}

export default function AdminProviders() {
  const [data, setData] = useState(null);
  const [hz, setHz] = useState(null);
  const [b2, setB2] = useState(null);
  const [b2keys, setB2keys] = useState({ key_id: "", app_key: "" });
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState({});

  const selftest = async (provider) => {
    setBusy(`test-${provider}`);
    setResult({ ...result, [provider]: null });
    try {
      const { data } = await api.post(`/admin/providers/${provider}/selftest`);
      setResult((r) => ({ ...r, [provider]: data }));
      data.ok ? toast.success(`${provider.toUpperCase()} live test passed`) : toast.error(`${provider.toUpperCase()} test failed`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const load = () => api.get("/admin/providers").then((r) => {
    setData(r.data);
    setHz({ ...r.data.hetzner, storage_backend: r.data.storage_backend });
    setB2({ ...r.data.b2 });
  });
  useEffect(() => { load(); }, []);
  if (!data || !hz || !b2) return <PageLoader />;

  const saveHz = async () => {
    setBusy("hz");
    try {
      await api.put("/admin/providers/hetzner", {
        enabled: hz.enabled, mode: hz.mode, server_type: hz.server_type,
        image: hz.image, storage_backend: hz.storage_backend,
      });
      toast.success("Hetzner provider saved"); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const saveB2 = async () => {
    setBusy("b2");
    try {
      await api.put("/admin/providers/b2", {
        enabled: b2.enabled, region: b2.region, endpoint: b2.endpoint,
        key_id: b2keys.key_id || null, app_key: b2keys.app_key || null,
      });
      toast.success("Backblaze B2 saved"); setB2keys({ key_id: "", app_key: "" }); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div>
      <div className="mb-2 flex items-center gap-2"><Cloud className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Infrastructure Providers</h1></div>
      <p className="mb-6 text-sm text-slate-400">Provider drivers behind the ZettaKeep abstraction. <b className="text-slate-200">DEV MOCK</b> simulates provisioning safely; <b className="text-cyan-300">LIVE</b> creates real cloud resources and bills against your provider account.</p>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Hetzner */}
        <GlassCard className="p-6" data-testid="provider-hetzner">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-display font-semibold text-slate-100">Hetzner Cloud — Compute</h2>
              <div className="mt-1"><Health h={hz.health} /></div>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${hz.mode === "live" ? "bg-cyan-500/15 text-cyan-300" : "bg-amber-500/15 text-amber-300"}`}>{hz.mode === "live" ? "LIVE" : "DEV MOCK"}</span>
          </div>

          {!hz.token_present && <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200">No HCLOUD_TOKEN in environment — add it to go live.</div>}

          <label className="flex items-center justify-between py-2 text-sm text-slate-300">Enabled
            <Switch checked={hz.enabled} onCheckedChange={(v) => setHz({ ...hz, enabled: v })} data-testid="hetzner-enabled" /></label>

          <div className="py-2">
            <label className="text-xs text-slate-500">Mode</label>
            <select value={hz.mode} onChange={(e) => setHz({ ...hz, mode: e.target.value })} data-testid="hetzner-mode" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
              <option value="mock">DEV MOCK (safe, no cost)</option>
              <option value="live">LIVE (real Hetzner servers)</option>
            </select>
          </div>
          {hz.mode === "live" && <div className="mb-2 flex items-start gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-2 text-xs text-cyan-200"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> New deployments will provision real Hetzner servers (cloud-init installs PBS 4.x and phones home). Real costs apply.</div>}

          <div className="grid grid-cols-2 gap-3 py-2">
            <div><label className="text-xs text-slate-500">Server type</label>
              <select value={hz.server_type} onChange={(e) => setHz({ ...hz, server_type: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
                {["cpx11", "cpx21", "cpx31", "cpx41", "cpx51"].map((t) => <option key={t}>{t}</option>)}
              </select></div>
            <div><label className="text-xs text-slate-500">Image</label>
              <Input value={hz.image} onChange={(e) => setHz({ ...hz, image: e.target.value })} className="mt-1 bg-slate-950 border-slate-700" /></div>
          </div>
          <div className="py-2">
            <label className="text-xs text-slate-500">Storage backend</label>
            <select value={hz.storage_backend} onChange={(e) => setHz({ ...hz, storage_backend: e.target.value })} data-testid="storage-backend" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
              <option value="block">Attached block volume (Hetzner)</option>
              <option value="b2">S3 object storage (Backblaze B2)</option>
            </select>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={saveHz} disabled={busy === "hz"} data-testid="save-hetzner" className="inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] disabled:opacity-60"><Save className="h-4 w-4" /> Save Hetzner</button>
            <button onClick={() => selftest("hetzner")} disabled={busy === "test-hetzner"} data-testid="test-hetzner" className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-cyan-500/50 disabled:opacity-60">{busy === "test-hetzner" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Live test</button>
          </div>
          {result.hetzner && <div className={`mt-2 text-xs ${result.hetzner.ok ? "text-emerald-400" : "text-rose-400"}`} data-testid="hetzner-test-result">{result.hetzner.ok ? "✓ " : "✗ "}{result.hetzner.detail}</div>}
        </GlassCard>

        {/* B2 */}
        <GlassCard className="p-6" data-testid="provider-b2">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-display font-semibold text-slate-100">Backblaze B2 — Object Storage</h2>
              <div className="mt-1"><Health h={b2.health} /></div>
            </div>
            <HardDrive className="h-6 w-6 text-slate-600" />
          </div>

          <label className="flex items-center justify-between py-2 text-sm text-slate-300">Enabled
            <Switch checked={b2.enabled} onCheckedChange={(v) => setB2({ ...b2, enabled: v })} data-testid="b2-enabled" /></label>

          <div className="grid grid-cols-2 gap-3 py-2">
            <div><label className="text-xs text-slate-500">Region</label><Input value={b2.region} onChange={(e) => setB2({ ...b2, region: e.target.value })} className="mt-1 bg-slate-950 border-slate-700" /></div>
            <div><label className="text-xs text-slate-500">S3 endpoint</label><Input value={b2.endpoint} onChange={(e) => setB2({ ...b2, endpoint: e.target.value })} className="mt-1 bg-slate-950 border-slate-700" /></div>
          </div>

          <div className="rounded-lg border border-slate-800 bg-[#06080D] p-3">
            <div className="text-xs text-slate-500">Master application key {b2.keys_present && <span className="ml-1 text-emerald-400">• stored</span>}</div>
            <Input placeholder="applicationKeyID" value={b2keys.key_id} onChange={(e) => setB2keys({ ...b2keys, key_id: e.target.value })} data-testid="b2-key-id" className="mt-2 bg-slate-950 border-slate-700" />
            <Input type="password" placeholder="applicationKey (paste to update)" value={b2keys.app_key} onChange={(e) => setB2keys({ ...b2keys, app_key: e.target.value })} data-testid="b2-app-key" className="mt-2 bg-slate-950 border-slate-700" />
            <p className="mt-2 text-xs text-slate-500">Keys are encrypted at rest. Used only to create per-customer private buckets + bucket-restricted keys.</p>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={saveB2} disabled={busy === "b2"} data-testid="save-b2" className="inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D] disabled:opacity-60"><Save className="h-4 w-4" /> Save B2</button>
            <button onClick={() => selftest("b2")} disabled={busy === "test-b2"} data-testid="test-b2" className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-cyan-500/50 disabled:opacity-60">{busy === "test-b2" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Live test</button>
          </div>
          {result.b2 && <div className={`mt-2 text-xs ${result.b2.ok ? "text-emerald-400" : "text-rose-400"}`} data-testid="b2-test-result">{result.b2.ok ? "✓ " : "✗ "}{result.b2.detail}</div>}
        </GlassCard>
      </div>

      <div className="mt-5 rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">
        <b className="text-slate-200">Scheduled monitoring is active</b> via platform crons: health checks (every 15 min), storage/PBS metrics (hourly), cost reconciliation + backup-health review (daily), capacity forecast (weekly). Live PBS metrics auto-activate on servers provisioned in LIVE mode.
      </div>
    </div>
  );
}
